import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

const ForecastChart = () => {
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

  const [data, setData] = useState([]);

  // Fetch real weather data for London with session storage caching
  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        // Check session storage first
        const cachedData = sessionStorage.getItem("londonWeatherData");
        const cachedTimestamp = sessionStorage.getItem("londonWeatherTimestamp");

        if (cachedData && cachedTimestamp) {
          const timestamp = Number.parseInt(cachedTimestamp, 10);
          const now = Date.now();

          // If cached data is less than 30 minutes old, use it
          if (now - timestamp < 30 * 60 * 1000) {
            setData(JSON.parse(cachedData));
            return;
          }
        }

        // Fetch new data from API
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?" +
            "latitude=51.5074&longitude=-0.1278&hourly=temperature_2m&timezone=Europe/London",
        );

        if (!response.ok) {
          throw new Error("Failed to fetch weather data");
        }

        const weatherData = await response.json();

        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        // Get current day and next 2 days. This automatically handles midnight rollover
        // ie, if it's Mon at 11:59 PM, the next update after 12:00 AM will show Tue, Wed, Thu
        const today = new Date();
        const days = [];
        for (let i = 0; i < 3; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          days.push(dayNames[date.getDay()]);
        }

        // Extract temperatures for the next 3 days at 2-hour intervals
        const processedData = days.map((day, dayIndex) => {
          const dayTemperatures = [];
          const startHour = dayIndex * 24; // Start from current day

          // Get temperatures at 2-hour intervals (0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22)
          for (let hour = 0; hour < 24; hour += 2) {
            const dataIndex = startHour + hour;
            if (weatherData.hourly?.temperature_2m?.[dataIndex]) {
              dayTemperatures.push(Math.round(weatherData.hourly.temperature_2m[dataIndex]));
            } else {
              dayTemperatures.push(0); // Fallback temperature if data not available
            }
          }

          return {
            day,
            temperatures: dayTemperatures,
          };
        });

        // Store in session storage with timestamp
        sessionStorage.setItem("londonWeatherData", JSON.stringify(processedData));
        sessionStorage.setItem("londonWeatherTimestamp", Date.now().toString());

        setData(processedData);
      } catch (error) {
        console.error("Error fetching weather data:", error);
        // Fallback to zero values if API fails - use correct dynamic days
        const today = new Date();
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const fallbackData = [];

        for (let i = 0; i < 3; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          fallbackData.push({
            day: dayNames[date.getDay()],
            temperatures: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          });
        }
        setData(fallbackData);
      }
    };

    // Fetch data immediately and then every 30 minutes
    fetchWeatherData();
    const interval = setInterval(fetchWeatherData, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const lineData = {
    // 00:00 to 22:00 in an array
    labels: Array.from({ length: 12 }, (_, i) => {
      const hour = i * 2;
      return hour.toString().padStart(2, "0") + ":00";
    }),
    datasets: data.map((dayData, index) => ({
      label: dayData.day,
      data: dayData.temperatures,
      borderColor: [
        "rgba(103, 227, 86, 1)", // Mon - Green
        "rgba(138, 86, 226, 1)", // Tue - Purple
        "#f80", // Wed - Orange
      ][index],
      backgroundColor: ["rgba(103, 227, 86, 0.1)", "rgba(138, 86, 226, 0.1)", "rgba(255, 136, 0, 0.1)"][index],
      tension: 0.4,
      borderWidth: 1.5,
      pointBackgroundColor: ["rgba(103, 227, 86, 1)", "rgba(138, 86, 226, 1)", "#f80"][index],
      pointBorderColor: "#fff",
      pointBorderWidth: 1,
      pointRadius: 4,
      pointHoverRadius: 6,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    plugins: {
      title: {
        display: true,
        text: "3 Day Forecast for London, UK",
        color: "rgba(255, 255, 255, 0.9)",
        font: {
          size: 16,
          weight: "bold",
        },
        padding: {
          top: 0,
          bottom: 2,
        },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "rgba(255, 255, 255, 0.9)",
          font: {
            size: 11,
          },
          usePointStyle: true,
          padding: 8,
          pointStyle: "circle",
          pointRadius: 3,
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "rgba(255, 255, 255, 1)",
        bodyColor: "rgba(255, 255, 255, 0.9)",
        borderColor: "rgba(255, 255, 255, 0.2)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function (context) {
            const dayLabel = context[0].dataset.label; // Gets the day, eg "Tue"
            const timeLabel = context[0].label; // Gets the time, eg "12:00"
            return `${dayLabel} ${timeLabel}`;
          },
          label: function (context) {
            return ` ${context.parsed.y}°C`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          // Spaces added to help centre the text
          text: "Time of Day                ",
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 12,
          },
        },
        ticks: {
          color: "rgba(255, 255, 255, 0.7)",
          maxRotation: 45,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
      y: {
        title: {
          display: true,
          text: "Temperature (°C)",
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 12,
          },
        },
        ticks: {
          color: "rgba(255, 255, 255, 0.7)",
          callback: function (value) {
            return value + "°C";
          },
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  return (
    <div style={{ padding: "12px 10px 12px 6px", height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: "280px",
          minWidth: "280px",
        }}
      >
        <Line data={lineData} options={chartOptions} />
      </div>
    </div>
  );
};

export default ForecastChart;
